import { Link } from 'react-router-dom';
import { useState } from 'react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const services = [
    { name: 'Software Development', href: '/software-development' },
    { name: 'Cloud Infrastructure', href: '/cloud-infrastructure' },
    { name: 'IT Support', href: '/it-support' },
    { name: 'Digital Transformation', href: '/services' }
  ];

  const company = [
    { name: 'About Us', href: '/about' },
    { name: 'Case Studies', href: '/case-studies' },
    { name: 'Careers', href: '/careers' },
    { name: 'Industries', href: '/industries' }
  ];

  const socialLinks = [
    { name: 'LinkedIn', icon: 'ri-linkedin-fill', href: 'https://linkedin.com/company/techcozoltd' },
    { name: 'Twitter', icon: 'ri-twitter-fill', href: 'https://twitter.com/techcozo' },
    { name: 'GitHub', icon: 'ri-github-fill', href: '#' },
    { name: 'Email', icon: 'ri-mail-fill', href: 'mailto:hello@techcozo.com' }
  ];

  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const response = await fetch(`${SUPABASE_URL}/functions/v1/techcozo-newsletter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: newsletterEmail }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setNewsletterEmail('');
        setTimeout(() => {
          setSubmitStatus('idle');
        }, 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900 text-white overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div
          className={`absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20`}
        ></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-green-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10">
        {/* Main Footer Content */}
        <div className="container mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-12">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <Link to="/" onClick={handleLinkClick} className="flex items-center space-x-3 mb-6 group">
                <img
                  src="https://static.readdy.ai/image/fa77cf8cc3bbd77c4d454e1d51d6ad11/34f7ef3fd990c9497763245b06aa73c4.png"
                  alt="TechCozo Logo"
                  className="h-12 w-12 object-contain transition-transform duration-300 group-hover:scale-110 bg-transparent rounded-xl"
                  style={{ backgroundColor: 'transparent' }}
                />
                <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 via-teal-400 to-green-400 bg-clip-text text-transparent">
                  TechCozo
                </span>
              </Link>

              <p className="text-white/80 mb-6 leading-relaxed max-w-md">
                Transforming businesses with innovative technology solutions. We deliver cutting‑edge software development, cloud infrastructure, and IT support services.
              </p>

              <div className="space-y-3">
                <div className="flex items-center space-x-3 group">
                  <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-gradient-to-r group-hover:from-blue-500 group-hover:to-teal-500 transition-all duration-300">
                    <i className="ri-map-pin-line text-white"></i>
                  </div>
                  <div>
                    <div className="text-white/60 text-sm">Address</div>
                    <div className="text-white">Caiden House, Canal Road, WA14 1TD</div>
                  </div>
                </div>

                <div className="flex items-center space-x-3 group">
                  <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-gradient-to-r group-hover:from-blue-500 group-hover:to-teal-500 transition-all duration-300">
                    <i className="ri-phone-line text-white"></i>
                  </div>
                  <div>
                    <div className="text-white/60 text-sm">Phone</div>
                    <a href="tel:01618837750" className="text-white hover:text-teal-300 transition-colors duration-300">
                      0161 883 7750
                    </a>
                  </div>
                </div>

                <div className="flex items-center space-x-3 group">
                  <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-gradient-to-r group-hover:from-blue-500 group-hover:to-teal-500 transition-all duration-300">
                    <i className="ri-mail-line text-white"></i>
                  </div>
                  <div>
                    <div className="text-white/60 text-sm">Email</div>
                    <a href="mailto:hello@techcozo.com" className="text-white hover:text-teal-300 transition-colors duration-300">
                      hello@techcozo.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-teal-400 to-green-400 bg-clip-text text-transparent">
                Services
              </h3>
              <ul className="space-y-3">
                {services.map((service) => (
                  <li key={service.name}>
                    <Link
                      to={service.href}
                      onClick={handleLinkClick}
                      className="text-white/80 hover:text-white hover:translate-x-2 transition-all duration-300 block"
                    >
                      {service.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-teal-400 to-green-400 bg-clip-text text-transparent">
                Company
              </h3>
              <ul className="space-y-3">
                {company.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      onClick={handleLinkClick}
                      className="text-white/80 hover:text-white hover:translate-x-2 transition-all duration-300 block"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                <h4 className="text-lg font-semibold mb-4 text-white">Follow Us</h4>
                <div className="flex space-x-3">
                  {socialLinks.map((social) => (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-gradient-to-r hover:from-blue-500 hover:via-teal-500 hover:to-green-500 transition-all duration-300 transform hover:scale-110"
                    >
                      <i className={`${social.icon} text-white`}></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="border-t border-white/10">
          <div className="container mx-auto px-6 py-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">Stay Updated</h3>
                <p className="text-white/80">
                  Get the latest insights on technology trends and business solutions delivered to your inbox.
                </p>
              </div>
              <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="email"
                    name="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    required
                    placeholder="Enter your email"
                    className="flex-1 px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-8 py-3 bg-gradient-to-r from-blue-500 via-teal-500 to-green-500 text-white rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </div>
                
                {submitStatus === 'success' && (
                  <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-3 backdrop-blur-sm">
                    <div className="flex items-center">
                      <i className="ri-check-circle-line text-green-300 mr-2"></i>
                      <p className="text-green-100 text-sm">
                        Thank you for subscribing! Check your email for confirmation.
                      </p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 backdrop-blur-sm">
                    <div className="flex items-center">
                      <i className="ri-error-warning-line text-red-300 mr-2"></i>
                      <p className="text-red-100 text-sm">
                        Sorry, there was an error. Please try again.
                      </p>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10">
          <div className="container mx-auto px-6 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-white/60 text-sm">
                © {currentYear} TechCozo Ltd. All rights reserved.
              </div>
              <div className="flex items-center space-x-6">
                <Link 
                  to="/privacy" 
                  onClick={handleLinkClick}
                  className="text-white/60 hover:text-white text-sm transition-colors duration-300"
                >
                  Privacy Policy
                </Link>
                <Link 
                  to="/terms" 
                  onClick={handleLinkClick}
                  className="text-white/60 hover:text-white text-sm transition-colors duration-300"
                >
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;